import React from 'react';
import { useStudioTypeSize } from '../StudioTypeScale';
import type { StudioPageContent } from '../../../constants';
import { studioCopy } from '../../../utils/contentCopy';
import LayoutFrame, { type LayoutContentProps } from './LayoutFrame';

interface StatHighlightContentProps extends Partial<LayoutContentProps> {
  content: StudioPageContent;
  format: string;
  locale: string;
}

const StatHighlightContent: React.FC<StatHighlightContentProps> = ({ content, format, locale, tokens }) => {
  const isStory = format === 'story';
  const t = tokens!;
  const typeSize = useStudioTypeSize();
  const chipLabel = studioCopy.statChip(content, locale);

  return (
    <div>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '5px 12px',
          borderRadius: t.radius.pill,
          background: t.chipBg,
          border: `1px solid ${t.hairline}`,
          color: t.muted,
          fontSize: typeSize(9.5),
          fontWeight: 700,
          letterSpacing: 1.4,
          textTransform: 'uppercase'
        }}
      >
        {chipLabel}
      </div>
      <div
        style={{
          margin: isStory ? '22px 0 10px' : '15px 0 7px',
          color: t.accent,
          fontSize: typeSize(isStory ? 69 : 55),
          fontWeight: 700,
          letterSpacing: '-2.4px',
          lineHeight: 1
        }}
      >
        {content.stat}
      </div>
      <div
        style={{
          maxWidth: 296,
          color: t.ink,
          fontSize: typeSize(isStory ? 19.5 : 16),
          fontWeight: 700,
          lineHeight: 1.32
        }}
      >
        {content.label}
      </div>
      <div
        style={{
          width: '100%',
          height: 1,
          margin: isStory ? '28px 0 22px' : '20px 0 16px',
          background: `linear-gradient(90deg, ${t.accent} 0%, ${t.hairline} 100%)`
        }}
      />
      {content.description && (
        <div
          style={{
            maxWidth: 296,
            padding: isStory ? '16px 17px' : '13px 15px',
            borderRadius: t.radius.lg,
            background: t.cardBg,
            border: `1px solid ${t.hairline}`,
            boxShadow: t.shadow,
            color: t.ink,
            fontSize: typeSize(isStory ? 12.5 : 11),
            fontWeight: 500,
            lineHeight: 1.6
          }}
        >
          {content.description}
        </div>
      )}
    </div>
  );
};

interface StatHighlightLayoutProps {
  width: number;
  height: number;
  format: string;
  locale?: string;
  content?: StudioPageContent;
  themeColor?: string;
  profileName?: string;
}

const StatHighlightLayout: React.FC<StatHighlightLayoutProps> = props => (
  <LayoutFrame {...props}>
    <StatHighlightContent
      content={props.content || {}}
      format={props.format}
      locale={props.locale || 'zh-Hant-HK'}
    />
  </LayoutFrame>
);

export default StatHighlightLayout;
